import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import { BLOG_SUB_CATEGORY_TYPE } from "utills/constants";
import { get } from "lodash";
import { IBlogDetail, IBlogForm, IBlogSubCategoryItem, IRelatedBlogItem } from "interfaces";
import CONFIG from "app-config/config.json";
import { RefreshIcon, XIcon } from "@heroicons/react/outline";
import { _generateSlug } from "utills/helpers";
import { useState } from "react";
import Dropzone from "components/dropzone";
import Textarea from "components/textarea";
import { TextEditor } from "components/textEditor";
import PreviewModal from "components/previewModal";
import DateTimePicker from "components/datetimePicker";
import moment from "moment";

export interface BlogFormProps {
  id?: string;
  defaultData?: IBlogDetail;
  onSubmit: (data: IBlogForm) => void;
}

const BlogSchema = z.object({
  is_active: z.boolean(),
  banner_url: z.string().min(1, "This field is required"),
  category: z.string().min(1, "This field is required"),
  content_en: z.string().min(1, "This field is required"),
  content_vi: z.string().min(1, "This field is required"),
  is_hot_position: z.boolean(),
  publish_at: z.string().min(1, "This field is required"),
  related_blog_ids: z.array(z.string()),
  short_description_en: z.string().min(1, "This field is required"),
  short_description_vi: z.string().min(1, "This field is required"),
  slug: z.string().min(1, "This field is required"),
  sub_category_id: z.string().optional().nullable(),
  tags: z.array(z.string()),
  title_en: z.string().min(1, "This field is required"),
  title_vi: z.string().min(1, "This field is required"),
});

export const BlogForm = ({ id, defaultData, onSubmit }: BlogFormProps) => {
  const [tagWord, setTagWord] = useState("");
  const [previewImageModalData, setPreviewImageModalData] = useState({
    isOpen: false,
    image: ""
  });
  const [contentLanguage, setContentLanguage] = useState("vi");

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IBlogForm, HttpError, IBlogForm>({
    defaultValues: {
      banner_url: defaultData?.banner_url ?? "",
      category: defaultData?.category ?? "",
      content_en: defaultData?.content_en ?? "",
      content_vi: defaultData?.content_vi ?? "",
      is_active: defaultData?.is_active ?? true,
      is_hot_position: defaultData?.is_hot_position ?? false,
      publish_at: defaultData?.publish_at ?? "",
      related_blog_ids: defaultData?.related_blog_ids ?? [],
      short_description_en: defaultData?.short_description_en ?? "",
      short_description_vi: defaultData?.short_description_vi ?? "",
      slug: defaultData?.slug ?? "",
      sub_category_id: defaultData?.sub_category_id ?? null,
      tags: defaultData?.tags ?? [],
      title_en: defaultData?.title_en ?? "",
      title_vi: defaultData?.title_vi ?? ""
    },
    resolver: zodResolver(BlogSchema),
  });

  const {
    data,
    isLoading,
  } = useOne<IBlogSubCategoryItem[]>({
    resource: `mkt/sub-categories/dropdown?category=${watch("category")}`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!watch("category"),
    } as any,
  });

  const {
    data: relatedBlogData,
    isLoading: isLoadingRelatedBlogs,
  } = useOne<IRelatedBlogItem[]>({
    resource: `mkt/blogs/related${id ? `?exclude_id=${id}` : ""}`,
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _renderGenerateSlugButton = () => {
    return (watch("title_en") || watch("title_vi")) ? (
      <div className="w-8 h-full flex items-center justify-center absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer">
        <RefreshIcon
          className="w-5 text-brand-500"
          onClick={() =>
            setValue(
              "slug",
              _generateSlug(
                watch("title_en") || watch("title_vi")
              ),
              {shouldValidate: true}
            )
          }
        />
      </div>
    ) : <></>;
  }

  const _addTag = (tag: string) => {
    const updatedTags = watch("tags");
    updatedTags.push(tag);
    setValue("tags", updatedTags);
  }
  const _removeTag = (index: number) => {
    let updatedTags = watch("tags");
    updatedTags.splice(index, 1);
    setValue("tags", updatedTags);
  }

  const _submit = (values: IBlogForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  const subCategoryArr = (data?.data ?? []).map((subCategory: IBlogSubCategoryItem) => (
    { value: subCategory.id, label: subCategory.name_en }
  ));

  const relatedBlogArr = (relatedBlogData?.data ?? []).map((blog: IRelatedBlogItem) => (
    { value: blog.id, label: blog.title_en }
  ));

  return (
    <div className="w-full ">
      <div>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
              <div className="sm:col-span-3">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <Label required id="is_hot_position" name="Hot position" />
                <Controller
                  control={control}
                  name="is_hot_position"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="is_hot_position"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_vi" name="Title vie" />
                <Input
                  id="title_vi"
                  type="text"
                  placeholder="Title vie"
                  error={errors["title_vi"]}
                  {...register("title_vi")}
                />
                {errors["title_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_en" name="Title eng" />
                <Input
                  id="title_en"
                  type="text"
                  placeholder="Title eng"
                  error={errors["title_en"]}
                  {...register("title_en")}
                />
                {errors["title_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="slug" name="Slug" />
                <div className="flex">
                  <div className="max-w-[50%] inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm break-all">
                    <p className="line-clamp-1">{CONFIG.DOMAIN}/news/detail/</p>
                  </div>
                  <div className="relative flex-1">
                    <Input
                      id="slug"
                      className="rounded-l-none pr-10"
                      placeholder="slug-name"
                      error={errors["slug"]}
                      {...register("slug")}
                    />
                    {_renderGenerateSlugButton()}
                  </div>
                </div>
                {errors["slug"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.slug.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="publish_at" name="Publish at" />
                <Controller
                  control={control}
                  name="publish_at"
                  render={({ field: { onChange, value = "" } }) => {
                    return (
                      <DateTimePicker
                        id="publish_at"
                        placeholder="Publish at"
                        className="bg-white"
                        error={get(
                          errors,
                          `publish_at.message`,
                          ""
                        )}
                        value={value}
                        onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                      />
                    );
                  }}
                />
                {errors["publish_at"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.publish_at.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="category" name="Category" />
                <Controller
                  control={control}
                  name={"category"}
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="category"
                        inputRef={ref}
                        options={BLOG_SUB_CATEGORY_TYPE}
                        id={`category`}
                        placeholder="Category"
                        defaultValue={
                          BLOG_SUB_CATEGORY_TYPE.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={BLOG_SUB_CATEGORY_TYPE.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                          setValue("sub_category_id", null, {shouldValidate: true})
                        }}
                        error={get(errors, `category`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `category`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `category.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="sub_category_id" name="Sub-category" />
                <Controller
                  control={control}
                  name="sub_category_id"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key={`sub_category_id__${watch("category")}`}
                        inputRef={ref}
                        options={subCategoryArr}
                        isLoading={isLoading}
                        id="sub_category_id"
                        placeholder="Sub-category"
                        isClearable={true}
                        defaultValue={
                          subCategoryArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={subCategoryArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: string }, {action} : any) => {
                          if (action === "clear") {
                            onChange(null);
                          } else {
                            onChange(selected?.value);
                          }
                        }}
                        error={get(errors, `sub_category_id`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `sub_category_id`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `sub_category_id.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="tags" name="Hashtag" />
                <Input
                  id="tags"
                  type="text"
                  placeholder="Hashtag"
                  value={tagWord}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagWord(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && tagWord) {
                      _addTag(tagWord);
                      setTagWord("");
                      // _handleSubmitFilter();
                    }
                  }}      
                />
                <div className="flex items-center flex-wrap gap-x-2">
                  {watch("tags").map((tag, index) => (
                    <div key={index} className="mt-2 max-w-full w-fit min-w-0 flex items-center gap-x-2 pl-3 pr-2.5 py-1.5 bg-brand-500 text-xs rounded-md text-white break-all">
                      <span className="min-w-0 break-all">{tag}</span> <XIcon className="w-4 h-4 shrink-0 hover:cursor-pointer" onClick={() => _removeTag(index)} />
                    </div>
                  ))}
                </div>
                {errors["tags"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.tags.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="banner_url" name="Banner (766x475)" required />
                <div>
                  <Controller
                    control={control}
                    name="banner_url"
                    render={({ field: { onChange, value = "" } }) => {
                      return (
                        <Dropzone
                          id="banner_url"
                          validator={{
                            width: 766,
                            height: 475,
                          }}
                          onChange={(imageUrl: string) => onChange(imageUrl)}
                          value={value}
                        />
                      );
                    }}
                  />
                  {watch("banner_url") ? (
                    <p
                      className="text-brand-500 underline text-sm font-medium mt-1 cursor-pointer w-fit"
                      onClick={() =>
                        setPreviewImageModalData({
                          isOpen: true,
                          image: watch("banner_url") ?? "",
                        })
                      }
                    >
                      Preview mobile image size
                    </p>
                  ) : (
                    <></>
                  )}
                </div>
                {errors["banner_url"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["banner_url"].message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full">
                <Label id="related_blog_ids" name="Related blogs" />
                <Controller
                  control={control}
                  name="related_blog_ids"
                  render={({ field: { onChange, value = [], ref } }) => (
                    <Select
                      isMulti
                      key={``}
                      inputRef={ref}
                      options={relatedBlogArr}
                      isLoading={isLoadingRelatedBlogs}
                      id="related_blog_ids"
                      placeholder="Select Partners"
                      value={value.map((id: string) => {
                        return relatedBlogArr.find((item) => item.value === id);
                      })}
                      onChange={(selected: { label: string; value: string }[]) =>
                        onChange(selected.map((item) => `${item.value}`))
                      }
                      error={get(
                        errors,
                        `related_blog_ids`,
                        ""
                      )}
                    />
                  )}
                />
                {errors["related_blog_ids"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["related_blog_ids"].message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="short_description_vi" name="Short Description vie" />
                <Textarea
                  id="short_description_vi"
                  placeholder="Short Description vie"
                  error={errors["short_description_vi"]}
                  {...register("short_description_vi")}
                />
                {errors["short_description_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.short_description_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="short_description_en" name="Short Description eng" />
                <Textarea
                  id="short_description_en"
                  placeholder="Short Description eng"
                  error={errors["short_description_en"]}
                  {...register("short_description_en")}
                />
                {errors["short_description_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.short_description_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full">
                <Label required id="content" name="Content" />
                <div>
                  <button 
                    className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors["title_vi"] || errors["content_vi"] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${contentLanguage === "vi" ? "bg-gray-300" : "bg-white"}`} 
                    onClick={() => setContentLanguage("vi")}
                  >
                    Vietnam
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors["title_en"] || errors["content_en"] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${contentLanguage === "en" ? "bg-gray-300" : "bg-white"}`}
                    onClick={() => setContentLanguage("en")}
                  >
                    English
                  </button>
                </div>
                {contentLanguage === "en" ? (
                  <div className="w-full">
                    <Controller
                      control={control}
                      name="content_en"
                      render={({ field: { onChange, value = "" } }) => (
                        <div key="content_en">
                          <TextEditor
                            value={value}
                            onChange={onChange}
                            toolbarSticky={true}
                            key="content_en"
                          />
                        </div>
                      )}
                    />
                    {errors["content_en"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors?.content_en.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <></>
                )}
                {contentLanguage === "vi" ? (
                  <div className="w-full">
                    <Controller
                      control={control}
                      name="content_vi"
                      render={({ field: { onChange, value = "" } }) => (
                        <div key="content_vi">
                          <TextEditor
                            value={value}
                            onChange={onChange}
                            toolbarSticky={true}
                            key="content_vi"
                          />
                        </div>
                      )}
                    />
                    {errors["content_vi"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors?.content_vi.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : <></>}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/mkt/blog${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit" onClick={handleSubmit(_submit)}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
      <PreviewModal 
        open={previewImageModalData.isOpen} 
        onClose={() => setPreviewImageModalData({
          ...previewImageModalData,
          isOpen: false
        })} 
        data={previewImageModalData.image}
      />
    </div>
  );
};
